import { useState, useCallback } from 'react';
import { ttsService } from '../services/tts';

type Accent = 'american' | 'british';

interface UsePronunciationReturn {
  playingAccent: Accent | null;
  isPlaying: boolean;
  playPronunciation: (text: string, accent?: Accent) => Promise<void>;
  playNormal: (text: string) => Promise<void>;
  playSlowly: (text: string) => Promise<void>;
}

/**
 * Hook for managing pronunciation playback
 * Handles TTS service calls and playing state
 */
export function usePronunciation(): UsePronunciationReturn {
  const [playingAccent, setPlayingAccent] = useState<Accent | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  /**
   * Play pronunciation with specified accent
   */
  const playPronunciation = useCallback(async (text: string, accent: Accent = 'american') => {
    if (playingAccent || isPlaying) return;

    setPlayingAccent(accent);
    setIsPlaying(true);

    const previousAccent = ttsService.getAccent();
    ttsService.setAccent(accent);

    try {
      await ttsService.speakNormal(text);
    } catch (error) {
      console.error('TTS error:', error);
    } finally {
      ttsService.setAccent(previousAccent);
      setPlayingAccent(null);
      setIsPlaying(false);
    }
  }, [playingAccent, isPlaying]);

  /**
   * Play text with normal speed
   */
  const playNormal = useCallback(async (text: string) => {
    if (isPlaying) return;

    setIsPlaying(true);
    try {
      await ttsService.speakNormal(text);
    } catch (error) {
      console.error('TTS error:', error);
    } finally {
      setIsPlaying(false);
    }
  }, [isPlaying]);

  /**
   * Play text slowly for learning
   */
  const playSlowly = useCallback(async (text: string) => {
    if (isPlaying) return;

    setIsPlaying(true);
    try {
      await ttsService.speakSlowly(text);
    } catch (error) {
      console.error('TTS error:', error);
    } finally {
      setIsPlaying(false);
    }
  }, [isPlaying]);

  return {
    playingAccent,
    isPlaying,
    playPronunciation,
    playNormal,
    playSlowly,
  };
}